import React, { useEffect, useState } from 'react'
import {
  Row,
  Col,
  Form,
  Modal,
  Input,
  Button,
  Select,
  DatePicker,
  Radio,
  TimePicker,
} from 'antd'
import '../styles/modal-styles.scss'
import closeIcon from 'assets/Images/close.svg'
import DateandTimeInputs from '../../../GeneralDetails/InputComps/DateandTimeInputs'
import TicketColors from '../TicketColors'
import {
  AdmissionTicketPayloadTypes,
  TicketsResp,
} from 'types/event/eventStep4'
import moment, { Moment } from 'moment'
import {
  convertMinutesToLocalTime,
  convertTimeToUtcMinutes,
} from 'helper/utility'
import {
  useCreateAdditionalTicket,
  useCreateAdmissionTicket,
} from 'api/EventCreation/CreationStep4'
import { creationEventStore } from 'state/event/eventCreationStore'
import ButtonSpinner from 'components/global/ButtonSpinner'
import { infoMessage } from 'api/global'
import { Photo, SpecialSchedule } from 'types/globalTypes'
import UploadDocument from 'components/global/UploadDocument'

interface AdmissionTicketModalProps {
  modalOpen: boolean
  modalClose: React.Dispatch<React.SetStateAction<boolean>>
  type: 'admission' | 'additional' | 'dropin' | 'discount'
  data?: TicketsResp
}

const AdmissionTicket: React.FC<AdmissionTicketModalProps> = ({
  modalOpen,
  modalClose,
  type,
  data,
}) => {
  const [form] = Form.useForm()
  const eventData = creationEventStore(store => store.eventData)
  const setEventData = creationEventStore(store => store.setEventData)
  const [selectedDates, setSelectedDates] = useState<string[]>([])
  const [specialScheduleData, setSpecialScheduleData] = useState([])
  const [ticketType, setTicketType] = useState<any>(
    data ? data.ticketType : undefined,
  )
  const [period, setPeriod] = useState<'weekly' | 'monthly' | undefined>(
    data ? data.period : undefined,
  )
  const [eventTime, setEventTime] = useState({
    startTime: undefined,
    endTime: undefined,
  })
  const [defaultTime, setDefaultTime] = useState({
    startTime: moment('09:00', 'HH:mm'),
    endTime: moment('15:00', 'HH:mm'),
  })

  const [ticketColor, setTicketColor] = useState<string>(
    data ? data.color : undefined,
  )
  const [admissionTicketId, setAdmissionTicketId] = useState<
    string | undefined
  >(type === 'additional' ? data?.allowedTicketsIds[0] : undefined)
  const [documentIds, setDocumentIds] = useState<string[]>(
    data?.photoId ? [data.photoId] : [],
  )
  const [photos, setPhotos] = useState<Photo[]>(data?.photo ? [data.photo] : [])

  const { mutate: admissionTicketMutate, isLoading: admissionTicketLoading } =
    useCreateAdmissionTicket()
  const { mutate: additionalTicketMutate, isLoading: additionalTicketLoading } =
    useCreateAdditionalTicket()

  const formSchema = {
    name: data ? data.name : undefined,
    ticketType: data ? data.ticketType : undefined,
    period: data && data.ticketType === 'grouped' ? data.period : undefined,
    expiry: data?.expiry ? moment(data.expiry) : undefined,
    ['expiry-time']: data?.expiry ? moment(data.expiry) : undefined,
    numberOfDays: data ? data?.numberOfDays : undefined,
    numberOfSelection: data ? data?.numberOfSelection : undefined,
    description: data ? data.description : undefined,
    price: data ? data.price : undefined,
    allowedTicketsIds: data ? data.allowedTicketsIds : [],
    guestAllowedToVisit: data ? data?.guestAllowedToVisit : 'all',
    minAge: data ? data?.minAge : undefined,
    maxAge: data ? data?.maxAge : undefined,
    limitPerFamily: data ? data?.limitPerFamily : undefined,
    totalLimit: data ? data?.totalLimit : undefined,
    // photoId: data ? data?.photoId : undefined,
  }
  const handleCancel = () => {
    modalClose(false)
  }

  const handleTicketType = (event: string) => {
    setTicketType(event)
  }

  const handlePeriodChange = (value: 'weekly' | 'monthly') => {
    setPeriod(value)
  }

  const handleAdmissionTicketChange = (value: string) => {
    setAdmissionTicketId(value)
  }

  const disableOutOfRangeDates = (current: Moment) => {
    const firstDate = moment(eventData.datesOfEvent[0]).startOf('day')
    const lastDate = moment(
      eventData.datesOfEvent[eventData.datesOfEvent.length - 1],
    ).endOf('day')
    return current < firstDate || current > lastDate
  }

  const onFinish = (values: AdmissionTicketPayloadTypes) => {
    if (!values.name) {
      return infoMessage('Name is required')
    }
    if (type === 'additional' && !values.allowedTicketsIds) {
      return infoMessage('Allowed ticket is required')
    }
    if (!values.ticketType) {
      return infoMessage('Ticket type is required')
    }
    if (values.ticketType === 'grouped' && !values.period) {
      return infoMessage('Period is required')
    }
    if (values.ticketType !== 'one-time' && selectedDates.length === 0) {
      return infoMessage('Ticket days and time is required')
    }
    if (values.price === undefined || values.price === null) {
      return infoMessage('Ticket price is required')
    }
    if (!ticketColor) {
      return infoMessage('Ticket color is required')
    }
    if (!values.guestAllowedToVisit) {
      return infoMessage('Guest allowed to visit is required')
    }
    if (values.numberOfDays && values.numberOfDays > selectedDates.length) {
      return infoMessage(
        'Max number of days must be equal to less than ticket days',
      )
    }
    if (ticketType === 'schedule' && values.numberOfDays > 7) {
      return infoMessage('Max number of days must be less than or equal to 7')
    }
    if (
      !values.numberOfDays &&
      (ticketType === 'open' || ticketType === 'schedule')
    ) {
      return infoMessage('Max number of days is required')
    }

    if (!values.numberOfSelection && ticketType === 'grouped') {
      return infoMessage('Number of selection is required')
    }

    if (
      ticketType === 'grouped' &&
      values.numberOfSelection &&
      selectedDates.length
    ) {
      const uniqueWeekKeys = new Set(
        selectedDates.map(date => moment(date).format('GGGG-[W]WW')),
      )
      const uniqueMonthKeys = new Set(
        selectedDates.map(date => moment(date).format('YYYY-MM')),
      )

      if (
        values.period === 'weekly' &&
        values.numberOfSelection > uniqueWeekKeys.size
      ) {
        return infoMessage(
          'Max number of weeks must be less than or equal to selected weeks',
        )
      }

      if (
        values.period === 'monthly' &&
        values.numberOfSelection > uniqueMonthKeys.size
      ) {
        return infoMessage(
          'Max number of months must be less than or equal to selected months',
        )
      }
    }

    const payload: AdmissionTicketPayloadTypes = {
      ...values,
      minAge: !values.minAge ? undefined : Number(values.minAge),
      maxAge: !values.maxAge ? undefined : Number(values.maxAge),
      limitPerFamily: !values.limitPerFamily
        ? undefined
        : Number(values.limitPerFamily),
      totalLimit: !values.totalLimit ? undefined : Number(values.totalLimit),
      color: ticketColor,
      // Keep the selected calendar day as-is. Going through `new Date(...Z)` here
      // reinterpreted the local date string as UTC, which shifted the day back one
      // for negative-offset (US) timezones (Mon–Fri saved as Sun–Thu). Parse the
      // YYYY-MM-DD string directly and set the start hour on it — same pattern as
      // the special-schedule mapping below — so the day never shifts.
      days: selectedDates.map((item: string) =>
        moment(item, 'YYYY-MM-DD')
          .set({
            hour: parseInt(moment(eventTime.startTime).utc().format('HH:mm'), 10),
            minute: 0,
            second: 0,
            millisecond: 0,
          })
          .format('YYYY-MM-DDTHH:mm:ss[Z]'),
      ),
      timings: {
        startTime: convertTimeToUtcMinutes(eventTime.startTime),
        endTime: convertTimeToUtcMinutes(eventTime.endTime),
        specialSchedules: specialScheduleData.map((data: SpecialSchedule) => ({
          ...data,
          day: moment(data.day, 'YYYY-MM-DD').format('YYYY-MM-DDTHH:mm:ss[Z]'),
        })),
      },
      expiry:
        values.expiry && values['expiry-time']
          ? moment
            .utc(
              `${moment(values.expiry).format('YYYY-MM-DD')} ${moment
                .utc(values['expiry-time'])
                .format('HH:mm')}`,
            )
            .format()
          : undefined,
      allowedTicketsIds: Array.isArray(values.allowedTicketsIds)
        ? values.allowedTicketsIds
        : [values.allowedTicketsIds],
      photoId: documentIds.length > 0 ? documentIds[0] : undefined,

      deleteObject: {
        minAge: values.minAge === '' ? 1 : undefined,
        maxAge: values.maxAge === '' ? 1 : undefined,
        limitPerFamily: values.limitPerFamily === '' ? 1 : undefined,
        totalLimit: values.totalLimit === '' ? 1 : undefined,
        photoId: !documentIds.length ? 1 : undefined,
      },
    }
    if (type === 'admission') {
      admissionTicketMutate(
        { payload: payload, eventId: eventData._id, ticketId: data?._id },
        {
          onSuccess: result => {
            setEventData({ ...eventData, ...result })
            modalClose(false)
          },
        },
      )
    } else if (type === 'additional') {
      additionalTicketMutate(
        { payload: payload, eventId: eventData._id, ticketId: data?._id },
        {
          onSuccess: result => {
            setEventData({ ...eventData, ...result })
            modalClose(false)
          },
        },
      )
    }
  }

  useEffect(() => {
    if (type !== 'dropin' && data && data.ticketType !== 'one-time') {
      setSelectedDates(
        (data as TicketsResp).days.map((date: string) =>
          moment(date).format('YYYY-MM-DD'),
        ),
      )
      setEventTime({
        startTime: moment(
          convertMinutesToLocalTime(
            (data as TicketsResp).timings.startTime,
            'HH:mm',
          ),
          'HH:mm',
        ),
        endTime: moment(
          convertMinutesToLocalTime(
            (data as TicketsResp).timings.endTime,
            'HH:mm',
          ),
          'HH:mm',
        ),
      })
    }
  }, [data])

  useEffect(() => {
    if (type !== 'dropin' && data && data.ticketType !== 'one-time') {
      setDefaultTime({
        startTime: moment(
          convertMinutesToLocalTime(
            (data as TicketsResp).timings.startTime,
            'HH:mm',
          ),
          'HH:mm',
        ),
        endTime: moment(
          convertMinutesToLocalTime(
            (data as TicketsResp).timings.endTime,
            'HH:mm',
          ),
          'HH:mm',
        ),
      })
    } else if (eventData?.timings) {
      setDefaultTime({
        startTime: moment(
          convertMinutesToLocalTime(eventData?.timings.startTime, 'HH:mm'),
          'HH:mm',
        ),
        endTime: moment(
          convertMinutesToLocalTime(eventData?.timings.endTime, 'HH:mm'),
          'HH:mm',
        ),
      })
    }
  }, [eventData?.timings])

  return (
    <div>
      <Modal
        open={modalOpen}
        onCancel={handleCancel}
        className="admission-ticket-modal"
        footer={false}
        centered
      >
        <div className="flex-center pt-20px justify-between ">
          <h2 className="w-600 text-18">
            {type === 'admission' ? 'Admission ticket' : 'Additional Ticket'}
          </h2>
          <img
            src={closeIcon}
            alt="close-button"
            onClick={handleCancel}
            className="close button pointer"
          />
        </div>
        <div className="h-30rem mt-20px overflow-auto-y overflow-hidden-x scroller">
          <Form
            form={form}
            layout="horizontal"
            initialValues={formSchema}
            onFinish={onFinish}
          >
            <Row gutter={10}>
              <Col>
                <label className="input-label required-input">
                  Ticket title
                </label>
              </Col>
              <Col>
                <Form.Item className="form-control" name="name">
                  <Input
                    placeholder="Ticket title"
                    className="form-control-field"
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={10}>
              <Col>
                <label className="input-label required-input">
                  Ticket type
                </label>
              </Col>
              <Col>
                <Form.Item className="pointer selector-input" name="ticketType">
                  <Select
                    className="select-input-field type-select-field"
                    placeholder="Select"
                    optionLabelProp="value"
                    onChange={handleTicketType}
                    options={[
                      {
                        value: 'open',
                        label: (
                          <>
                            <h1 className="text-16 w-500">Open</h1>{' '}
                            <p className="grey-txt">
                              Any number of days within period
                            </p>
                          </>
                        ),
                      },
                      {
                        value: 'grouped',
                        label: (
                          <>
                            <h1 className="text-16 w-500">Grouped</h1>{' '}
                            <p className="grey-txt">
                              Weekly or monthly tickets
                            </p>
                          </>
                        ),
                      },
                      {
                        value: 'schedule',
                        label: (
                          <>
                            <h1 className="text-16 w-500">Schedule</h1>{' '}
                            <p className="grey-txt">
                              Day of week can choose</p>
                          </>
                        ),
                      },
                      {
                        value: 'preselected',
                        label: (
                          <>
                            <h1 className="text-16 w-500">Preselected</h1>{' '}
                            <p className="grey-txt">
                              Define the exact days of tickets
                            </p>
                          </>
                        ),
                      },
                      {
                        value: 'one-time',
                        label: (
                          <>
                            <h1 className="text-16 w-500">One-time</h1>{' '}
                            <p className="grey-txt">
                              Does not required date selection
                            </p>
                          </>
                        ),
                      },

                    ]}
                  />
                </Form.Item>
              </Col>
            </Row>

            {ticketType === 'grouped' && (
              <Row>
                <Col>
                  <label className="input-label required-input">Period</label>
                </Col>
                <Col>
                  <Form.Item className="pointer selector-input" name="period">
                    <Select
                      className="select-input-field type-select-field"
                      placeholder="Select"
                      optionLabelProp="value"
                      onChange={handlePeriodChange}
                      options={[
                        {
                          value: 'monthly',
                          label: (
                            <>
                              <p>Monthly</p>
                            </>
                          ),
                        },
                        {
                          value: 'weekly',
                          label: (
                            <>
                              <p>Weekly</p>
                            </>
                          ),
                        },
                      ]}
                    />
                  </Form.Item>
                </Col>
              </Row>
            )}

            {type === 'additional' && (
              <Row gutter={10}>
                <Col>
                  <label className="input-label required-input">
                    Allowed tickets
                  </label>
                </Col>
                <Col>
                  <Form.Item
                    className="pointer selector-input"
                    name="allowedTicketsIds"
                  >
                    <Select
                      className="select-input-field type-select-field"
                      placeholder="Select"
                      optionLabelProp="label"
                      options={eventData.admissionTickets.map(
                        (ticket: TicketsResp) => {
                          return {
                            value: ticket._id,
                            label: ticket.name,
                          }
                        },
                      )}
                      onChange={handleAdmissionTicketChange}
                    />
                  </Form.Item>
                </Col>
              </Row>
            )}

            <Row>
              <Col span={24}>
                <label htmlFor="" className="input-label">
                  Expiration date
                </label>
                <Form.Item name="expiry">
                  <DatePicker
                    className="w-100 form-control-field"
                    placeholder="Expiration date"
                    disabledDate={disableOutOfRangeDates}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row>
              <Col span={24}>
                <label htmlFor="" className="input-label">
                  Expiration Time
                </label>
                <Form.Item name="expiry-time">
                  <TimePicker
                    format={'hh:mm a'}
                    className="w-100 form-control-field"
                    placeholder="Expiration time"
                  />
                </Form.Item>
              </Col>
            </Row>

            {ticketType !== 'one-time' && (
              <DateandTimeInputs
                selectedDates={selectedDates}
                setSelectedDates={setSelectedDates}
                specialScheduleData={specialScheduleData}
                setSpecialScheduleData={setSpecialScheduleData}
                eventTime={eventTime}
                setEventTime={setEventTime}
                type={type}
                allowedTicketId={admissionTicketId}
                defaultTime={defaultTime}
                setDefaultTime={setDefaultTime}
              />
            )}

            {(ticketType === 'open' || ticketType === 'schedule') && (
              <Row className="mt-20px">
                <Col className="d-flex align-items-center">
                  <div
                    className="relative"
                    style={{ width: '100%', marginTop: '-14px' }}
                  >
                    <span className="custom-field-label required-input">
                      Max Registration days
                    </span>
                  </div>
                  <Form.Item name="numberOfDays" style={{ width: '20%' }}>
                    <Input
                      type="number"
                      min={0}
                      placeholder="0"
                      className="text-end br-12"
                      onWheel={e => e.currentTarget.blur()}
                    />
                  </Form.Item>
                </Col>
              </Row>
            )}

            {ticketType === 'grouped' && (
              <Row className="mt-20px">
                <Col className="d-flex align-items-center">
                  <div
                    className="relative"
                    style={{ width: '100%', marginTop: '-14px' }}
                  >
                    <span className="custom-field-label required-input">
                      {`Max Registration ${
                        period === 'weekly'
                        ? 'weeks'
                        : period === 'monthly'
                          ? 'months'
                          : ''
                        }`}
                    </span>
                  </div>
                  <Form.Item name="numberOfSelection" style={{ width: '20%' }}>
                    <Input
                      type="number"
                      min={0}
                      placeholder="0"
                      className="text-end br-12"
                      onWheel={e => e.currentTarget.blur()}
                    />
                  </Form.Item>
                </Col>
              </Row>
            )}

            <Row className="mt-10px">
              <Col span={24}>
                <label htmlFor="" className="custom-field-label">
                  Description
                </label>
                <Form.Item name="description">
                  <Input.TextArea
                    className="note-field"
                    placeholder="Terms of usage of the ticket"
                    autoSize={{ minRows: 3, maxRows: 10 }}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row>
              <Col className="d-flex align-items-center">
                <div
                  className="relative"
                  style={{ width: '100%', marginTop: '-14px' }}
                >
                  <span className="custom-field-label required-input">
                    Price
                  </span>
                </div>
                <Form.Item name="price" style={{ width: '20%' }}>
                  <Input
                    type="number"
                    placeholder="0"
                    className="text-end br-12"
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row>
              <Col span={24}>
                <label className="input-label required-input">
                  Select colors
                </label>
                <TicketColors
                  ticketColor={ticketColor}
                  setTicketColor={setTicketColor}
                />
              </Col>
            </Row>

            <Row className="mt-20px">
              <Col span={24}>
                <label className="input-label required-input">
                  Guests allowed to visit
                </label>
                <Form.Item
                  className="form-control mb-0"
                  name="guestAllowedToVisit"
                >
                  <Radio.Group>
                    <Radio value={'all'} className="mr-40 radio">
                      All
                    </Radio>
                    <Radio value={'boys'} className="mr-40 radio">
                      Boys
                    </Radio>
                    <Radio value={'girls'} className="mr-40 radio">
                      Girls
                    </Radio>
                  </Radio.Group>
                </Form.Item>
              </Col>
            </Row>

            <div className="w-80p admission-ticket-inputs mt-20px">
              <Row>
                <Col>
                  <h4 className="b1 mb-15">Guests limitation</h4>
                </Col>
                <Col className="d-flex align-items-center">
                  <div style={{ width: '100%', marginTop: '-14px' }}>
                    <span>Age restrictions (min)</span>
                  </div>
                  <Form.Item name="minAge" style={{ width: '30%' }}>
                    <Input
                      type="number"
                      placeholder="0"
                      className="text-end br-5"
                      onWheel={e => e.currentTarget.blur()}
                      onChange={e => {
                        const value = e.target.value
                          ? Number(e.target.value)
                          : undefined
                      }}
                    />
                  </Form.Item>
                </Col>
                <Col className="d-flex align-items-center">
                  <div style={{ width: '100%', marginTop: '-14px' }}>
                    <span>Age restrictions (max)</span>
                  </div>
                  <Form.Item name="maxAge" style={{ width: '30%' }}>
                    <Input
                      type="number"
                      placeholder="0"
                      className="text-end br-5"
                      onWheel={e => e.currentTarget.blur()}
                    />
                  </Form.Item>
                </Col>
                <Col className="d-flex align-items-center">
                  <div style={{ width: '100%', marginTop: '-14px' }}>
                    <span>Limit per Family</span>
                  </div>
                  <Form.Item name="limitPerFamily" style={{ width: '30%' }}>
                    <Input
                      type="number"
                      placeholder="0"
                      className="text-end br-5"
                      onWheel={e => e.currentTarget.blur()}
                    />
                  </Form.Item>
                </Col>
                <Col className="d-flex align-items-center">
                  <div style={{ width: '100%', marginTop: '-14px' }}>
                    <span>Total tickets limit</span>
                  </div>
                  <Form.Item name="totalLimit" style={{ width: '30%' }}>
                    <Input
                      type="totalTicket"
                      placeholder="0"
                      className="text-end br-5 radius-12"
                    />
                  </Form.Item>
                </Col>
              </Row>
            </div>

            <div className="w-80p">
              <label className="input-label">Add image</label>
              <UploadDocument
                documentIds={documentIds}
                setDocumentIds={setDocumentIds}
                multiple={false}
                docType="image/*"
                files={photos}
              />
            </div>

            <Row justify="end" className="mt-20px">
              <Button
                className="stepper-button"
                disabled={admissionTicketLoading || additionalTicketLoading}
                htmlType="submit"
              >
                {admissionTicketLoading || additionalTicketLoading ? (
                  <ButtonSpinner size={20} color="#000" />
                ) : (
                  'Save'
                )}
              </Button>
            </Row>
          </Form>
        </div>
      </Modal>
    </div>
  )
}

export default AdmissionTicket
